/**
 * Footer Component
 * Contains legal links and company information
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  Divider,
  Grid
} from '@mui/material';
import {
  FamilyRestroom,
  Email,
  Phone
} from '@mui/icons-material';

function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.100',
        py: 6,
        mt: 'auto'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ 
          flexDirection: { xs: 'column', md: 'row' },
          '& .MuiGrid-item': {
            width: { xs: '100%', md: 'auto' }
          }
        }}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FamilyRestroom sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                Earn-E Chore Helper
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Making family life easier through smart chore management, 
              rewards, and family organization.
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 2 }}>
              Features
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link 
                href="/#features" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Task Management
              </Link>
              <Link 
                href="/#features" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Rewards & Allowances
              </Link>
              <Link 
                href="/#features" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Shopping Lists
              </Link>
              <Link 
                href="/#features" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Recipe Collection
              </Link>
              <Link 
                href="/#features" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Family Calendar
              </Link>
            </Box>
          </Grid>

          {/* Support */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 2 }}>
              Support
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link 
                href="/help" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Help Center
              </Link>
              <Link 
                href="/contact" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Contact Us
              </Link>
              <Link 
                href="/faq" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                FAQ
              </Link>
              <Link 
                href="/feedback" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Feedback
              </Link>
            </Box>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 2 }}>
              Resources
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link 
                href="/blog" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Blog
              </Link>
              <Link 
                href="/blog" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Free Parenting Tips
              </Link>
              <Link 
                href="/#features" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                How It Works
              </Link>
            </Box>
          </Grid>

          {/* Legal */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 2 }}>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link 
                href="/privacy-policy" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: 'flex', 
                  alignItems: 'center',
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Privacy Policy
              </Link>
              <Link 
                href="/terms-of-service" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Terms of Service
              </Link>
              <Link 
                href="/cookie-policy" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                Cookie Policy
              </Link>
              <Link 
                href="/coppa-compliance" 
                color="text.secondary" 
                underline="hover" 
                sx={{ 
                  minHeight: 48, 
                  minWidth: 48,
                  display: "flex", 
                  alignItems: "center",
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  py: 1.5,
                  px: 0.5
                }}
              >
                COPPA Compliance
              </Link>
            </Box>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Contact
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Email sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} color="action" />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                >
                  support@earn-echore.app
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Phone sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} color="action" />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                >
                  +1 (219) 280-0438
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Bottom Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            © {currentYear} Earn-E Chore Helper. All rights reserved.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Made with ❤️ for families
            </Typography>
            <Typography variant="body2" color="text.secondary">
              COPPA Compliant
            </Typography>
          </Box>
        </Box>

        {/* Additional Compliance Notice */}
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: 'info.dark', 
          borderRadius: 1,
          textAlign: 'center'
        }}>
          <Typography variant="caption" sx={{ color: 'white' }}>
            <strong>Privacy Notice:</strong> We are committed to protecting your family's privacy. 
            Our app is fully compliant with COPPA. Children under 13 cannot create accounts independently. 
            Generative AI features are restricted to parents (18+) or users 13-17 with verifiable parental 
            consent, in accordance with Google's API policies.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
